import React, { useState, useEffect, useContext } from 'react';
import { Search, Filter, RefreshCw, Eye, Edit, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappings
const FEEDBACK_TYPES = {
  bug_critical: { label: '🟥 Bug critique', color: 'destructive' },
  bug: { label: '🟧 Bug', color: 'orange' },
  ux: { label: '🟨 UX', color: 'yellow' },
  suggestion: { label: '🟦 Suggestion', color: 'blue' },
  feature: { label: '🟩 Fonctionnalité', color: 'green' }
};

const FEEDBACK_STATUSES = {
  new: { label: 'Nouveau', color: 'default' },
  in_review: { label: 'En révision', color: 'blue' },
  planned: { label: 'Planifié', color: 'purple' },
  in_progress: { label: 'En cours', color: 'yellow' },
  resolved: { label: 'Résolu', color: 'green' },
  closed: { label: 'Fermé', color: 'secondary' }
};

const FeedbackManager = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    admin_notes: ''
  });

  // Fetch feedbacks
  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/admin/beta/feedback?${params.toString()}`, {
        withCredentials: true
      });

      setFeedbacks(response.data.feedbacks);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
      toast.error('Erreur lors du chargement des feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchFeedbacks();
    }
  }, [user, filters]);

  // Open update modal
  const handleEdit = (feedback) => {
    setSelectedFeedback(feedback);
    setUpdateData({
      status: feedback.status,
      admin_notes: feedback.admin_notes || ''
    });
    setUpdateModalOpen(true);
  };

  // Submit update
  const handleUpdate = async () => {
    if (!selectedFeedback) return;

    try {
      await axios.patch(
        `${API}/admin/beta/feedback/${selectedFeedback.id}`,
        updateData,
        { withCredentials: true }
      );

      toast.success('Feedback mis à jour avec succès');
      setUpdateModalOpen(false);
      fetchFeedbacks();
    } catch (error) {
      console.error('Failed to update feedback:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Create incident from feedback
  const handleCreateIncident = () => {
    if (!selectedFeedback) return;
    
    // Prepare incident data from feedback
    const incidentData = {
      title: selectedFeedback.title,
      description: selectedFeedback.description,
      category: 'UX', // Default category, admin can change
      priority: selectedFeedback.type === 'bug_critical' ? 'critical' : 
                selectedFeedback.type === 'bug' ? 'high' : 'medium',
      origin: 'feedback',
      origin_feedback_id: selectedFeedback.id,
      browser_info: selectedFeedback.browser_info,
      app_version: selectedFeedback.app_version,
      screenshots: selectedFeedback.screenshot_url ? [selectedFeedback.screenshot_url] : []
    };
    
    // Navigate to incident manager with pre-filled data
    // Store in sessionStorage for the incident manager to pick up
    sessionStorage.setItem('create_incident_from_feedback', JSON.stringify(incidentData));
    window.location.href = '/beta-incidents';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Accès réservé aux administrateurs
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Gestion des Feedbacks Beta</h1>
        <p className="text-muted-foreground mt-2">
          Centre de gestion des retours utilisateurs et bugs reportés
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Nouveaux
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.new}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                En révision
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.in_review}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Planifiés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.planned}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                En cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.in_progress}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Résolus
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.resolved}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Fermés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.closed}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher dans titre/description..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Type filter */}
            <Select
              value={filters.type}
              onValueChange={(value) => setFilters({ ...filters, type: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(FEEDBACK_TYPES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(FEEDBACK_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" size="sm" onClick={fetchFeedbacks}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feedbacks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Feedbacks ({feedbacks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Chargement...</p>
          ) : feedbacks.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Aucun feedback trouvé
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Titre</TableHead>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>
                        <Badge variant="outline">
                          {FEEDBACK_TYPES[feedback.type]?.label || feedback.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate font-medium">{feedback.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {feedback.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{feedback.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={FEEDBACK_STATUSES[feedback.status]?.color || 'default'}>
                          {FEEDBACK_STATUSES[feedback.status]?.label || feedback.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(feedback.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(feedback)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Modal */}
      <Dialog open={updateModalOpen} onOpenChange={setUpdateModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Modifier le feedback</DialogTitle>
            <DialogDescription>
              {selectedFeedback?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Create Incident Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateIncident}
                className="gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Créer un incident depuis ce feedback
              </Button>
            </div>
            {/* Full description */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Description complète</Label>
              <p className="text-sm p-3 bg-muted rounded-md">
                {selectedFeedback?.description}
              </p>
            </div>

            {/* Context info */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Informations de contexte</Label>
              <div className="text-xs space-y-1 p-3 bg-muted rounded-md">
                <p><strong>Navigateur :</strong> {selectedFeedback?.browser_info}</p>
                <p><strong>Page :</strong> {selectedFeedback?.page_url}</p>
                <p><strong>Résolution :</strong> {selectedFeedback?.screen_size}</p>
                <p><strong>Langue :</strong> {selectedFeedback?.language}</p>
              </div>
            </div>

            {/* Screenshot */}
            {selectedFeedback?.screenshot_url && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Capture d'écran</Label>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}${selectedFeedback.screenshot_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Voir la capture →
                </a>
              </div>
            )}

            {/* Status update */}
            <div className="space-y-2">
              <Label htmlFor="update-status">Statut</Label>
              <Select
                value={updateData.status}
                onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
              >
                <SelectTrigger id="update-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FEEDBACK_STATUSES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Admin notes */}
            <div className="space-y-2">
              <Label htmlFor="update-notes">Notes administrateur</Label>
              <Textarea
                id="update-notes"
                placeholder="Notes internes, actions prises, etc..."
                value={updateData.admin_notes}
                onChange={(e) => setUpdateData({ ...updateData, admin_notes: e.target.value })}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackManager;
